import type { ReactNode } from 'react';
import { Search } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Search-as-you-type over a product list.
 *
 * The same control on the catalogue, on sales and on purchases, because they
 * are the same act — "find me the thing with this on it" — and a box that
 * behaves differently on each page is three things to learn instead of one.
 * Debouncing is the caller's job, via `useDebounced`.
 *
 * No submit button. A button implies the list is not already showing what you
 * asked for, which after the first keystroke it is.
 */
export function SearchBox({
  value,
  onChange,
  label,
  placeholder = 'Search name or barcode',
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  placeholder?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <div className={cn('relative min-w-52', className)}>
      <Search
        size={14}
        aria-hidden
        className="absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-faint"
      />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className="w-full rounded border border-rule bg-surface py-1.5 pr-2 pl-8 text-xs outline-none focus:border-accent"
      />
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <section className={cn('qs-panel overflow-hidden', className)}>
      {title && (
        <header className="flex items-center justify-between border-b border-rule px-3 py-2">
          <h2 className="qs-eyebrow">{title}</h2>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * Direction indicator.
 *
 * The glyph carries the meaning and the colour reinforces it, never the other
 * way round — so this survives colour blindness, a monochrome print and a phone
 * screen in sunlight.
 */
export function Delta({ percent }: { percent: string | null }): React.JSX.Element {
  if (percent === null) {
    return <span className="text-2xs text-ink-faint">no comparison</span>;
  }

  const value = Number(percent);
  const up = value >= 0;

  return (
    <span
      className={cn('qs-figure text-xs', up ? 'text-up' : 'text-down')}
      aria-label={`${up ? 'Up' : 'Down'} ${Math.abs(value)} percent`}
    >
      <span aria-hidden>{up ? '▲' : '▼'}</span> {Math.abs(value).toFixed(1)}%
    </span>
  );
}

/**
 * The foot of a paginated list.
 *
 * Renders nothing when there is nothing more, which is the point: its absence
 * is the only honest signal that a list is complete. Several lists used to stop
 * at a hard limit with no foot at all, so "that is everything" and "that is the
 * first fifty" looked identical.
 *
 * The count is included because it answers the question the button provokes —
 * "how much am I looking at?" — without a second trip.
 */
export function LoadMore({
  shown,
  hasMore,
  isLoading,
  onLoad,
  noun = 'shown',
}: {
  shown: number;
  hasMore: boolean;
  isLoading: boolean;
  onLoad: () => void;
  noun?: string;
}): React.JSX.Element | null {
  if (!hasMore) return null;

  return (
    <div className="border-t border-rule p-2 text-center">
      <button
        type="button"
        onClick={onLoad}
        disabled={isLoading}
        className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:border-rule-strong hover:text-ink disabled:opacity-60"
      >
        {isLoading ? 'Loading…' : `Load more (${shown.toLocaleString()} ${noun})`}
      </button>
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }): React.JSX.Element {
  return <p className="px-3 py-6 text-center text-xs text-ink-faint">{children}</p>;
}

/**
 * A load in progress.
 *
 * Was a line of static text, which is indistinguishable from a page that has
 * stopped. The bar underneath is indeterminate on purpose — the API returns a
 * page at a time and there is no total to show a fraction of, and a progress bar
 * that invents a fraction is worse than one that admits it does not know.
 */
export function Spinner({ label }: { label: string }): React.JSX.Element {
  return (
    <div className="px-3 py-8 text-center" role="status">
      <p className="text-xs text-ink-faint">{label}</p>
      <span className="mx-auto mt-2 block h-0.5 w-24 overflow-hidden rounded-full bg-rule">
        <span className="qs-indeterminate block h-full w-1/3 rounded-full bg-accent" />
      </span>
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }): React.JSX.Element {
  return (
    <p role="alert" className="qs-panel p-3 text-xs text-down">
      {error instanceof Error ? error.message : 'Something went wrong.'}
    </p>
  );
}

/**
 * How a basket was settled.
 *
 * Rendered as chips rather than a comma list so a split payment is visible at a
 * glance in a column of single-word cells — `Voucher + Credit` reads as two
 * things, `Voucher, Credit` reads as a wrapped sentence.
 *
 * Credit is styled apart from the rest because it is categorically different:
 * every other chip is money already in the drawer, and Credit is money owed. A
 * manager scanning this column for exposure should not have to read the word.
 */
export function TenderChips({ methods }: { methods: string[] }): React.JSX.Element {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {methods.map((method, index) => (
        <span key={method} className="flex items-center gap-1">
          {index > 0 && (
            <span className="text-2xs text-ink-faint" aria-hidden>
              +
            </span>
          )}
          <span
            className={cn(
              'rounded border px-1.5 py-0.5 text-2xs whitespace-nowrap',
              method === 'Credit'
                ? 'border-warn/40 bg-warn-soft text-warn'
                : 'border-rule bg-sunken text-ink-muted',
            )}
          >
            {method}
          </span>
        </span>
      ))}
    </span>
  );
}

const FLAG_LABELS: Record<string, string> = {
  reorder: 'Reorder',
  overstock: 'Overstock',
  deadstock: 'Dead stock',
  negative: 'Negative stock',
};

export function Tag({ value }: { value: string }): React.JSX.Element {
  // Stock flags are states, not series, so they use the reserved warning colour
  // with a written label rather than a colour the reader has to decode.
  const isProblem = value === 'negative' || value === 'deadstock';

  return (
    <span
      className={cn(
        'rounded border px-1.5 py-0.5 text-2xs whitespace-nowrap',
        isProblem
          ? 'border-down/30 bg-down/5 text-down'
          : 'border-warn/30 bg-warn-soft text-warn',
      )}
    >
      {FLAG_LABELS[value] ?? value}
    </span>
  );
}
